/**
 * EU AI Act — domain constants for the AI Use Case Register's risk-classification,
 * accountability and review vocabularies (Regulation (EU) 2024/1689).
 *
 * A sibling of `aiGovernance.ts` rather than an extension of it. That file is a 1:1 port
 * whose copy is the product; this file is net-new AI Act vocabulary, and keeping the two
 * apart means the ported copy is never edited to make room for new fields.
 *
 * The GDPR and transfer vocabularies this register needs are **re-exported here, not
 * copied**. Personal data and third-country transfers must have one vocabulary across the
 * product: a second list of transfer mechanisms or DPIA verdicts would fork the domain model
 * and make a transfer mean different things in the ROPA and in an AI use case. If a value
 * needs changing it changes at its source and every register follows.
 *
 * Badge maps are Nimbus design-system tone names, rendered as:
 *   <span className={`np-chip np-tone-${tone}`}>…</span>
 *
 * Mirrored server-side in `lambda/prod-nimbus-ai-use-cases/lambda_function.py`. The closed
 * vocabularies below are validated there too — the backend is the authority.
 */

import type {
  AiActClassification,
  ActRole,
  ApprovalDecision,
  HumanOversight,
  Monitoring,
  ReviewFrequency,
} from '../types/models';

// ─── Re-exported, single source of truth in privacy.ts ────────────────────────
// Chapter V transfers and the DPIA verdict are the same concepts here as in the Record of
// Processing Activities, including the rule that a transfer with no mechanism blocks
// activation. `privacy.ts` in turn re-exports the GDPR vocabularies from `aiGovernance.ts`,
// so there is one chain and no copies.
export {
  TRANSFER_MECHANISMS,
  TRANSFER_MECHANISM_LABELS,
  TRANSFER_MECHANISM_TONE,
  DPIA_VERDICT_LABELS,
  DPIA_VERDICT_TONE,
  hasUnlawfulTransfer,
} from './privacy';

// ─── Risk classification — the tier that gates the flow ──────────────────────

export const AI_ACT_CLASSIFICATIONS: AiActClassification[] = [
  'prohibited', 'high_risk', 'limited', 'minimal',
];

export const AI_ACT_CLASSIFICATION_LABELS: Record<AiActClassification, string> = {
  '':           'Not yet classified',
  'prohibited': 'Prohibited',
  'high_risk':  'High risk',
  'limited':    'Limited risk',
  'minimal':    'Minimal risk',
};

export const AI_ACT_CLASSIFICATION_TONE: Record<AiActClassification, string> = {
  '':           'neutral',
  // Deliberately loud, and it blocks activation server-side: a prohibited practice is not a
  // risk to be managed, it is a system that must not be built.
  'prohibited': 'danger',
  'high_risk':  'warning',
  'limited':    'info',
  'minimal':    'success',
};

export const AI_ACT_CLASSIFICATION_ICONS: Record<AiActClassification, string> = {
  '':           'ri-question-line',
  'prohibited': 'ri-forbid-2-line',
  'high_risk':  'ri-error-warning-line',
  'limited':    'ri-eye-line',
  'minimal':    'ri-checkbox-circle-line',
};

/**
 * Plain-English tier descriptions for the classification step's choice cards.
 *
 * Written for someone who has never read the Regulation and should not have to. `article` is
 * the reference to cite, not the explanation — the explanation is `short` and `detail`.
 * `examples` are illustrative, not a checklist: a system can be high-risk without matching
 * any of them, which is why `classificationRationale` is a required field and not optional.
 */
export const AI_ACT_CLASSIFICATION_DESCRIPTIONS: Record<
  AiActClassification,
  { short: string; detail: string; article: string; examples: string[] }
> = {
  '': {
    short: 'Nobody has decided yet.',
    detail: 'This use case has not been classified. It is not the same as minimal risk — it means the question has not been answered, so nothing downstream can rely on it.',
    article: '—',
    examples: [],
  },
  'prohibited': {
    short: 'Banned. This must not be built or used at all.',
    detail: 'A small set of uses are outlawed outright, whatever safeguards you put around them. There is no approval path, no mitigation and no conditional sign-off: if a use case lands here, it stops.',
    article: 'Art 5',
    examples: [
      'Scoring people socially to decide how they are treated elsewhere',
      'Inferring emotions of employees or students',
      'Scraping facial images indiscriminately to build a recognition database',
    ],
  },
  'high_risk': {
    short: 'Allowed, with the heaviest obligations in the Act.',
    detail: 'The AI makes or materially informs decisions that affect people’s access to work, money, education, essential services, justice or safety — or it is a safety component of a regulated product. Expect documented risk management, data governance, logging, accuracy testing, real human oversight and, as a provider, a conformity assessment before it goes live.',
    article: 'Annex III',
    examples: [
      'Screening or ranking job applicants',
      'Deciding creditworthiness or insurance pricing',
      'Marking exams or allocating school places',
    ],
  },
  'limited': {
    short: 'Allowed. You must tell people they are dealing with AI.',
    detail: 'The obligation here is disclosure rather than approval. People interacting with the system, or seeing what it produces, have to be able to tell it is AI — which means the disclosure has to exist in the product, not only in this record.',
    article: 'Art 50',
    examples: [
      'A customer-facing chatbot',
      'AI-generated images, audio or video shown to others',
      'Emotion recognition or biometric categorisation outside the banned contexts',
    ],
  },
  'minimal': {
    short: 'Allowed, with no AI-Act-specific obligations.',
    detail: 'Most internal productivity uses land here. The Act asks nothing extra of them — but GDPR, your own AI policy and any confidentiality obligations still apply in full, which is why the rest of this record is still worth completing.',
    article: '—',
    examples: [
      'Drafting or summarising internal documents',
      'Spam and anomaly filtering',
      'Code completion for developers',
    ],
  },
};

/**
 * Explicit severity ranking, ascending. Needed because every natural alternative is wrong:
 * sorting by label gives High → Limited → Minimal → Prohibited (alphabetical), and sorting by
 * the union's declaration order is invisible to `Array.sort`.
 *
 * `''` ranks lowest because it is the *absence* of a claim, not a claim of safety. Do not read
 * that as "unclassified is fine" — an unclassified system is the real exposure on a compliance
 * dashboard, and it needs its own count or filter (`isClassified`) rather than a severity rank
 * that would either bury it at the bottom of the table or libel it at the top.
 */
export const AI_ACT_CLASSIFICATION_SEVERITY: Record<AiActClassification, number> = {
  '':           0,
  'minimal':    1,
  'limited':    2,
  'high_risk':  3,
  'prohibited': 4,
};

// ─── GPAI — a separate axis, not a fifth tier ────────────────────────────────
//
// General-purpose AI obligations sit alongside the tier: a system can be Annex III high-risk
// *and* built on a general-purpose model, or minimal-risk and GPAI. The record stores two
// booleans (`isGpai`, `isGpaiSystemic`); this tier is a *display* derivation of them, which is
// why it lives here and not in models.ts. Never persist it, and never let it replace the tier.

export type GpaiTier = 'none' | 'gpai' | 'gpai_systemic';

export const GPAI_TIERS: GpaiTier[] = ['none', 'gpai', 'gpai_systemic'];

export const GPAI_TIER_LABELS: Record<GpaiTier, string> = {
  'none':          'Not general-purpose AI',
  'gpai':          'General-purpose AI',
  'gpai_systemic': 'General-purpose AI — systemic risk',
};

export const GPAI_TIER_TONE: Record<GpaiTier, string> = {
  'none':          'neutral',
  'gpai':          'info',
  'gpai_systemic': 'warning',
};

export const GPAI_TIER_ICONS: Record<GpaiTier, string> = {
  'none':          'ri-subtract-line',
  'gpai':          'ri-cpu-line',
  'gpai_systemic': 'ri-radar-line',
};

export const GPAI_TIER_DESCRIPTIONS: Record<
  GpaiTier,
  { short: string; detail: string; article: string }
> = {
  'none': {
    short: 'Purpose-built, not a general-purpose model.',
    detail: 'The system does one job it was built for, rather than being a broadly capable model adapted to this use.',
    article: '—',
  },
  'gpai': {
    short: 'Built on a broadly capable model.',
    detail: 'The use case runs on a general-purpose model — a large language or multimodal model that can serve many unrelated purposes. Providers of those models owe documentation, copyright and training-data transparency obligations; as a deployer you mainly need to know which model it is and record it under Vendors.',
    article: 'Art 53',
  },
  'gpai_systemic': {
    short: 'A general-purpose model at the frontier of capability.',
    detail: 'A small number of the largest models carry extra obligations on evaluation, adversarial testing, incident reporting and cybersecurity. Only mark this where the model provider has itself been designated — it is a statement about the model, not about how much you rely on it.',
    article: 'Art 55',
  },
};

// ─── Role in the value chain ─────────────────────────────────────────────────

export const ACT_ROLES: ActRole[] = ['provider', 'deployer', 'both'];

export const ACT_ROLE_LABELS: Record<ActRole, string> = {
  '':         'Role not recorded',
  'provider': 'Provider',
  'deployer': 'Deployer',
  'both':     'Provider and deployer',
};

export const ACT_ROLE_TONE: Record<ActRole, string> = {
  '':         'neutral',
  'provider': 'warning',
  'deployer': 'info',
  'both':     'warning',
};

export const ACT_ROLE_ICONS: Record<ActRole, string> = {
  '':         'ri-question-line',
  'provider': 'ri-tools-line',
  'deployer': 'ri-play-circle-line',
  'both':     'ri-shuffle-line',
};

export const ACT_ROLE_DESCRIPTIONS: Record<ActRole, { short: string; detail: string }> = {
  '': {
    short: 'Not answered yet.',
    detail: 'Which obligations attach depends entirely on this answer, so it cannot be left blank on a submitted record.',
  },
  'provider': {
    short: 'We build it, or put our name on it, and make it available to others.',
    detail: 'The heavier of the two positions: providers carry the conformity, documentation and post-market monitoring duties. Note you can become a provider of someone else\'s system by putting your brand on it or substantially modifying it — that is the trap most organisations fall into.',
  },
  'deployer': {
    short: 'We use an AI system someone else provides, under our own authority.',
    detail: 'Deployer duties are lighter but real: use the system as instructed, keep human oversight in place, keep logs, and tell people where disclosure is required.',
  },
  'both': {
    short: 'Both at once.',
    detail: 'The usual shape for product work: you deploy a vendor\'s model and, in doing so, provide an AI system to your own customers. Assume both sets of obligations apply.',
  },
};

// ─── Automated decision-making ───────────────────────────────────────────────

/** A yes/no governance flag with the copy explaining why it is being asked. */
export interface AdmFlagSpec {
  /** The AiUseCase key this flag writes to. */
  field: 'makesAutomatedDecisions' | 'involvesProfiling';
  label: string;
  /** Helper copy — what a "yes" actually means, in plain terms. */
  description: string;
  /** What answering yes triggers. Shown when the flag is set, not as scare copy up front. */
  trigger: string;
  tone: string;
  icon: string;
}

/**
 * The two questions the register never asked. Between them they are the trigger for GDPR
 * Art 22, for an AI Act Annex III determination and for most internal AI policies — which is
 * why the port plan calls this the single largest gap in the module.
 */
export const ADM_FLAGS: AdmFlagSpec[] = [
  {
    field: 'makesAutomatedDecisions',
    label: 'Does this AI make decisions about people, or materially inform them?',
    description: 'Say yes if a person\'s outcome — a job, a price, a limit, a service, a sanction — depends on what the AI produces. "A human signs it off" is still yes if the human is in practice following the AI.',
    trigger: 'Answering yes usually means an Annex III look at the high-risk tier, a documented human-oversight arrangement, and a GDPR Art 22 position on solely-automated decisions.',
    tone: 'warning',
    icon: 'ri-scales-3-line',
  },
  {
    field: 'involvesProfiling',
    label: 'Does it evaluate or predict personal characteristics?',
    description: 'Scoring, ranking, segmenting or predicting anything about a person — behaviour, reliability, interests, health, location, performance. It counts even when no decision follows immediately.',
    trigger: 'Profiling raises the bar on the lawful basis, and is one of the criteria that tips processing into needing a DPIA.',
    tone: 'warning',
    icon: 'ri-user-search-line',
  },
];

// ─── Human oversight (Art 14) ────────────────────────────────────────────────

/** One oversight capability, as a guided form field. */
export interface HumanOversightControlSpec {
  /** The HumanOversight key this control writes to. */
  field: 'hasApprovalGate' | 'hasOverride' | 'hasKillSwitch';
  label: string;
  description: string;
  tone: string;
  icon: string;
}

/**
 * Three separate capabilities, in increasing bluntness. They are deliberately not a scale and
 * not substitutes: a kill switch answers "can we stop it", which is a different question from
 * "can we stop it doing this one thing to this one person".
 */
export const HUMAN_OVERSIGHT_CONTROLS: HumanOversightControlSpec[] = [
  {
    field: 'hasApprovalGate',
    label: 'A human approves before the output takes effect',
    description: 'Someone reviews and releases the AI\'s output rather than it acting straight away. Be honest about volume — an approval step nobody has time to use is not an approval gate.',
    tone: 'success',
    icon: 'ri-check-double-line',
  },
  {
    field: 'hasOverride',
    label: 'A human can override or reverse an individual outcome',
    description: 'A named person can change the result for one case, and the people affected have a route to ask for that.',
    tone: 'info',
    icon: 'ri-arrow-go-back-line',
  },
  {
    field: 'hasKillSwitch',
    label: 'The system can be stopped outright',
    description: 'Someone can switch it off without a release cycle or a vendor ticket, and it is clear who that is and what happens to work in flight.',
    tone: 'warning',
    icon: 'ri-stop-circle-line',
  },
];

// ─── Vendors and models ──────────────────────────────────────────────────────

/**
 * Suggestions for the vendor "Hosting region" input (datalist / quick-pick). Where inference
 * runs is what turns a vendor row into a Chapter V transfer question, so the list is phrased
 * as places rather than product tiers.
 */
export const HOSTING_REGION_SUGGESTIONS: string[] = [
  'EU / EEA',
  'EU only (contractual data residency)',
  'United Kingdom',
  'United States',
  'Switzerland',
  'Norway',
  'Global / not guaranteed',
  'On-premise / self-hosted',
];

// ─── Post-market monitoring (Art 72) ─────────────────────────────────────────

/** One of the three monitoring prose fields, as a guided form field. */
export interface MonitoringFieldSpec {
  field: keyof Monitoring;
  label: string;
  guidance: string;
  placeholder: string;
}

export const MONITORING_FIELDS: MonitoringFieldSpec[] = [
  {
    field: 'incidentThresholds',
    label: 'What counts as an incident here?',
    guidance: 'Describe the point at which something becomes reportable rather than annoying — a wrong outcome for a person, a confidentiality breach, a rate of errors, an outage. Name the threshold, not just the categories.',
    placeholder: 'e.g. any incorrect eligibility decision reaching a customer; more than 2% of outputs rejected at review in a week…',
  },
  {
    field: 'driftDetection',
    label: 'How would you notice it getting worse?',
    guidance: 'Models degrade quietly as inputs, data or the vendor\'s model change underneath you. Say what is measured, how often, and who looks at it.',
    placeholder: 'e.g. weekly sample of 50 outputs reviewed by the support lead; monthly comparison of approval rates against the pre-launch baseline…',
  },
  {
    field: 'feedbackLoop',
    label: 'What happens to what you find?',
    guidance: 'The part most records miss. Where do complaints, overrides and review findings go, who acts on them, and what triggers a re-approval of this use case?',
    placeholder: 'e.g. findings logged as issues against this use case; two consecutive breached thresholds reopen the record for re-approval…',
  },
];

// ─── Periodic review ─────────────────────────────────────────────────────────
//
// These are the platform's existing frequency values, matching
// `components/CollectionScheduleCard.tsx:22` exactly. The platform had three mutually
// incompatible frequency vocabularies before this; adding a fourth would mean a use case and
// an evidence collection could not share a scheduler.
//
// ⚠️ UNRESOLVED MISMATCH WITH THE BACKEND — do not paper over it in a page.
// `prod-nimbus-ai-use-cases/lambda_function.py` currently validates a *different* set:
// REVIEW_INTERVAL_MONTHS = monthly | quarterly | semi_annual | annual | biennial — snake_case,
// whole months only, so it has no Weekly/Bi-weekly and adds a 24-month biennial. Send
// 'Quarterly' to it today and it does not validate. The sets differ, not just the casing, so
// this needs a product decision (is a weekly AI review meaningful? is biennial?) rather than a
// mapping layer: a translation table here would be the fourth vocabulary by another name.
// Whichever side moves, both must end up on one list.

export const REVIEW_FREQUENCIES: ReviewFrequency[] = [
  'Weekly', 'Bi-weekly', 'Monthly', 'Quarterly', 'Bi-annually', 'Annually',
];

export const REVIEW_FREQUENCY_LABELS: Record<ReviewFrequency, string> = {
  '':             'Select one',
  'Weekly':       'Weekly',
  'Bi-weekly':    'Bi-weekly',
  'Monthly':      'Monthly',
  'Quarterly':    'Quarterly',
  'Bi-annually':  'Bi-annually',
  'Annually':     'Annually',
};

/**
 * Interval lookups for computing `nextReviewDate`. Split into days and months because the two
 * need different arithmetic: month steps have to clamp to the end of a short month (31 Jan +
 * 1 month is 28 Feb, not 3 Mar).
 *
 * Copy `addInterval` from `CollectionScheduleCard.tsx:50-64`, which does all of it in UTC and
 * documents why. Do **not** copy the variant in `MaintenanceTasks.tsx:80-91` — it mixes UTC
 * parsing with local-time setters and drifts by a day across a DST boundary.
 */
export const REVIEW_FREQUENCY_DAYS: Partial<Record<ReviewFrequency, number>> = {
  'Weekly': 7,
  'Bi-weekly': 14,
};

export const REVIEW_FREQUENCY_MONTHS: Partial<Record<ReviewFrequency, number>> = {
  'Monthly': 1,
  'Quarterly': 3,
  'Bi-annually': 6,
  'Annually': 12,
};

/**
 * Suggested starting cadence per tier, for prefilling the field — a default, not a rule. The
 * user can change it, and nothing should enforce it: review frequency is a policy decision,
 * and hard-coding one here would quietly become the policy.
 */
export const SUGGESTED_REVIEW_FREQUENCY: Record<AiActClassification, ReviewFrequency> = {
  '':           '',
  'prohibited': '',            // nothing to schedule — a prohibited use case does not go live
  'high_risk':  'Quarterly',
  'limited':    'Bi-annually',
  'minimal':    'Annually',
};

// ─── Approval decision ───────────────────────────────────────────────────────

// Mirrors DECISION_VALUES in the AI use case Lambda. These are never picked in a form — the
// transition endpoint stamps them from the verified caller — so the array exists for filters
// and legends, not for a <select>.
export const APPROVAL_DECISIONS: ApprovalDecision[] = [
  'approved', 'approved_with_conditions', 'rejected',
];

export const APPROVAL_DECISION_LABELS: Record<ApprovalDecision, string> = {
  '':                         'No decision recorded',
  'approved':                 'Approved',
  'approved_with_conditions': 'Approved with conditions',
  'rejected':                 'Rejected',
};

export const APPROVAL_DECISION_TONE: Record<ApprovalDecision, string> = {
  '':                         'neutral',
  'approved':                 'success',
  // An approval, but not an unqualified one: the conditions are part of the decision and the
  // chip should not read the same as a clean approval.
  'approved_with_conditions': 'warning',
  'rejected':                 'danger',
};

export const APPROVAL_DECISION_ICONS: Record<ApprovalDecision, string> = {
  '':                         'ri-time-line',
  'approved':                 'ri-shield-check-line',
  'approved_with_conditions': 'ri-shield-flash-line',
  'rejected':                 'ri-close-circle-line',
};

/** Decisions that carry free text — the conditions, or the reason for a refusal. Mirrors
 *  DECISIONS_WITH_TEXT server-side, which rejects a refusal with no reason. */
export const DECISIONS_WITH_TEXT: ApprovalDecision[] = ['approved_with_conditions', 'rejected'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * A prohibited practice — blocks activation. The one tier that is not a risk to be managed,
 * modelled as a helper rather than an inline `=== 'prohibited'` so the rule has one home, the
 * way `hasUnlawfulTransfer` does for a transfer with no Chapter V basis.
 */
export const isBlocking = (classification: AiActClassification): boolean =>
  classification === 'prohibited';

/** Has anyone actually classified this? `''` is unanswered, not minimal. */
export const isClassified = (classification: AiActClassification): boolean =>
  classification !== '';

/** Severity rank of a tier — see AI_ACT_CLASSIFICATION_SEVERITY for why this is explicit. */
export const classificationSeverity = (classification: AiActClassification): number =>
  AI_ACT_CLASSIFICATION_SEVERITY[classification] ?? 0;

/** Comparator for "worst first" ordering. Pass to `Array.prototype.sort` directly. */
export const byClassificationSeverityDesc = (
  a: { aiActClassification: AiActClassification },
  b: { aiActClassification: AiActClassification },
): number => classificationSeverity(b.aiActClassification) - classificationSeverity(a.aiActClassification);

/**
 * The display tier for the two stored GPAI booleans. `isGpaiSystemic` is only meaningful when
 * `isGpai` is set, so a record carrying systemic without the base flag resolves to `'none'`
 * rather than inventing a state — the base flag is the one that governs.
 */
export const gpaiTier = (uc: { isGpai: boolean; isGpaiSystemic: boolean }): GpaiTier => {
  if (!uc.isGpai) return 'none';
  return uc.isGpaiSystemic ? 'gpai_systemic' : 'gpai';
};

/**
 * Whether `automatedDecisionDetails` is required. One rule, four surfaces — the wizard's
 * `canAdvance`, Step 7's asterisk, the detail page's Required marker, and the PDF.
 *
 * **`makesAutomatedDecisions` only, never profiling.** This returned `makesAutomatedDecisions
 * || involvesProfiling` until 2026-08-04, and the two surfaces that used it disagreed with the
 * two that did not:
 *
 * - The server clears the field unless `makesAutomatedDecisions` (`lambda_function.py:835-836`)
 *   and requires it on the same condition alone (`:886-887`). So for a profiling-only record
 *   the field cannot hold a value at all — the detail page rendered a Required marker over
 *   "Not provided" that no amount of typing could ever satisfy, and the PDF printed the field
 *   empty. The wizard sidestepped it by inlining the narrow rule and leaving a comment saying
 *   this helper was "deliberately not used here", which is how the drift survived review.
 * - It is also the wrong question to ask. The field is *"what is decided, about whom"*, which
 *   presupposes a decision. Profiling with no decision attached is not GDPR Art 22; what it
 *   does trigger is a DPIA, and that is where its detail belongs — exactly what the flag's own
 *   trigger copy in `ADM_FLAGS` says.
 *
 * `involvesProfiling` therefore stays a recorded flag that feeds DPIA screening, and stops
 * being a gate on a field it can never fill.
 */
export const needsAutomatedDecisionDetails = (uc: {
  makesAutomatedDecisions: boolean;
}): boolean => uc.makesAutomatedDecisions;

/**
 * Whether this tier requires a linked AI Risk Assessment record before submission — currently
 * `high_risk` only. One definition so the wizard's gate, the panel's "required" badge, the
 * detail page and the PDF can never quietly disagree with each other, the same reasoning behind
 * `needsAutomatedDecisionDetails` above (see its own comment for the drift that helper was
 * added to stop).
 */
export const requiresAiRiskAssessment = (classification: AiActClassification): boolean =>
  classification === 'high_risk';

/** No oversight capability recorded at all — the state Art 14 exists to prevent. */
export const hasAnyOversight = (oversight: HumanOversight): boolean =>
  oversight.hasApprovalGate || oversight.hasOverride || oversight.hasKillSwitch;

/**
 * Approved at all — **both** approval members. The bug this exists to prevent is a surface
 * testing `decision === 'approved'`, which silently treats every conditionally-approved system
 * as unapproved (or vice versa on an inverted test).
 */
export const isApproved = (decision: ApprovalDecision): boolean =>
  decision === 'approved' || decision === 'approved_with_conditions';

/** Approved, but with conditions attached that someone has to actually discharge. */
export const isConditionalApproval = (decision: ApprovalDecision): boolean =>
  decision === 'approved_with_conditions';

/** True when `decisionConditions` must be non-empty for this decision. */
export const decisionRequiresText = (decision: ApprovalDecision): boolean =>
  DECISIONS_WITH_TEXT.includes(decision);

// ─── Blank-value factories ───────────────────────────────────────────────────
//
// `HumanOversight` and `Monitoring` are always-present objects on the record, so `||`-style
// defaulting does not type-check and a partial spread silently omits keys. These give the
// wizard's `withDefaults`, the detail page and the report one shape to agree on. Repeating
// rows (`vendors`, `transfers`) keep their blank row inline at the `useRepeatableRows`
// call site, as `ProcessingActivityDetail.tsx:196-199` already does.

export const emptyHumanOversight = (): HumanOversight => ({
  hasApprovalGate: false,
  hasOverride: false,
  hasKillSwitch: false,
  accountablePersonId: '',
  escalationPath: '',
});

export const emptyMonitoring = (): Monitoring => ({
  incidentThresholds: '',
  driftDetection: '',
  feedbackLoop: '',
});
